///
/// EarthBrowser
///
/// Created by Matt Giger on 10/25/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Elevation under the cutout footprint: fetches the land elevation tiles
/// that cover the block's lat/lon bounds at a zoom coarse enough for a
/// handful of tiles, plus the bathymetry tiles (ETOPO 2022, z4-z5, see
/// tools/bathymetry) for the sea floor, and exposes one bilinear sampler
/// over both. Land tiles are the authority above sea level; where they
/// read ocean (0) the bathymetry supplies the negative depth. Missing
/// tiles sample as 0.
///

import { ElevationContourService } from "@/earthbrowser/utils/ElevationContourService"
import { type TileId } from "@/earthbrowser/activities/EarthActivity/tilecache/TileId"
import { ElevationLoader } from "@/earthbrowser/utils/loaders/ElevationLoader"
import { geographic, wgs } from "@/earthbrowser/utils/geographic"
import { trig } from "@stovokor/math/math"

const cMaxTiles = 16
const cTileServerHost = "https://tiles.earthbrowser.com"
/** Bathymetry tiles exist at these levels only (about the ETOPO 60" resolution) */
const cBathyMinLevel = 4
const cBathyMaxLevel = 5
/** Stored bathymetry value = meters + offset; 0 = no data (tools/bathymetry/build.ts) */
const cBathyOffset = 12000
/** Dev override: URL template with {z} {y} {x} for locally built tiles */
const cBathyUrlOverrideKey = "earthbrowser_bathymetry_url"

interface LoadedTile {
	data: Uint16Array
	size: number
}

/**
 * One zoom level of 256-px tiles on the elevation grid (rows 2^z, cols
 * 2^(z+1)) with bilinear sampling across tile seams
 */
class TileGrid {
	private readonly tiles = new Map<string, LoadedTile>()
	private readonly cols: number
	private readonly rows: number
	private tileSize = 256

	constructor(public readonly level: number, private readonly decode: (raw: number) => number | undefined) {
		this.rows = 1 << level
		this.cols = 1 << (level + 1)
	}

	public get tileCount(): number {
		return this.tiles.size
	}

	public add(tileId: TileId, data: Uint16Array): void {
		const size = Math.round(Math.sqrt(data.length))
		if(size * size !== data.length) return
		this.tileSize = size
		this.tiles.set(`${tileId.y}_${tileId.x}`, { data, size })
	}

	public clear(): void {
		this.tiles.clear()
	}

	/** Bilinear sample; undefined where no tile or no data covers the point */
	public sample(latRad: number, lonRad: number): number | undefined {
		if(this.tiles.size === 0) return undefined
		const size = this.tileSize
		const gx = ((lonRad + trig.pi) / trig.two_pi) * this.cols * size - 0.5
		const gy = ((trig.half_pi - latRad) / trig.pi) * this.rows * size - 0.5
		const x0 = Math.floor(gx)
		const y0 = Math.floor(gy)
		const fx = gx - x0
		const fy = gy - y0
		const v00 = this.pixel(x0, y0)
		const v10 = this.pixel(x0 + 1, y0)
		const v01 = this.pixel(x0, y0 + 1)
		const v11 = this.pixel(x0 + 1, y0 + 1)
		if(v00 === undefined || v10 === undefined || v01 === undefined || v11 === undefined) {
			return v00 ?? v10 ?? v01 ?? v11
		}
		const top = v00 * (1 - fx) + v10 * fx
		const bottom = v01 * (1 - fx) + v11 * fx
		return top * (1 - fy) + bottom * fy
	}

	private pixel(gx: number, gy: number): number | undefined {
		const size = this.tileSize
		const totalX = this.cols * size
		const totalY = this.rows * size
		const wx = ((gx % totalX) + totalX) % totalX
		const cy = Math.min(totalY - 1, Math.max(0, gy))
		const tileX = Math.floor(wx / size)
		const tileY = Math.floor(cy / size)
		const tile = this.tiles.get(`${tileY}_${tileX}`)
		if(!tile) return undefined
		const px = wx - tileX * size
		const py = cy - tileY * size
		return this.decode(tile.data[py * tile.size + px])
	}
}

export class CutoutElevation {
	private readonly land: TileGrid
	private readonly bathy: TileGrid
	private aborted = false

	private constructor(landLevel: number, bathyLevel: number) {
		// Land set: unsigned meters, >= 32768 is no-data / ocean
		this.land = new TileGrid(landLevel, raw => raw >= 32768 ? 0 : raw)
		// Bathymetry set: offset meters, 0 is no-data
		this.bathy = new TileGrid(bathyLevel, raw => raw === 0 ? undefined : raw - cBathyOffset)
	}

	/**
	 * Load coverage for a footprint of the given half-extents (km) around
	 * the center. Resolves once every tile request has settled; tiles that
	 * fail or don't exist sample as ocean / no data
	 */
	public static async load(centerLatDeg: number, centerLonDeg: number, halfEastKm: number, halfNorthKm: number): Promise<CutoutElevation> {
		const center = new geographic(centerLonDeg * trig.rad, centerLatDeg * trig.rad, 0)
		const radiusKm = Math.hypot(halfEastKm, halfNorthKm)
		const landLevel = ElevationContourService.calculateOptimalTileLevel(center, radiusKm, cMaxTiles)
		const bathyLevel = Math.min(cBathyMaxLevel, Math.max(cBathyMinLevel, landLevel))
		const latSpan = halfNorthKm / wgs.a
		const lonSpan = halfEastKm / (wgs.a * Math.max(Math.cos(center.latitude), 0.05))

		const elevation = new CutoutElevation(landLevel, bathyLevel)
		const landIds = ElevationContourService.getTilesForRegion(center, lonSpan, latSpan, landLevel)
		const bathyIds = ElevationContourService.getTilesForRegion(center, lonSpan, latSpan, bathyLevel)
		await Promise.all([
			...landIds.map(async tileId => {
				const data = await ElevationContourService.loadElevationTile(tileId)
				if(data && !elevation.aborted) elevation.land.add(tileId, data)
			}),
			...bathyIds.map(async tileId => {
				const data = await new ElevationLoader(bathymetryTileUrl(tileId)).loadData()
				if(data && !elevation.aborted) elevation.bathy.add(tileId, data)
			})
		])
		return elevation
	}

	public get tileCount(): number {
		return this.land.tileCount
	}

	public get bathymetryTileCount(): number {
		return this.bathy.tileCount
	}

	public get level(): number {
		return this.land.level
	}

	public destroy(): void {
		this.aborted = true
		this.land.clear()
		this.bathy.clear()
	}

	/**
	 * Elevation in meters at a geographic point (radians): land tiles above
	 * sea level, bathymetry (negative) where the land set reads ocean, 0
	 * where neither has data
	 */
	public sampleMeters(latRad: number, lonRad: number): number {
		const land = this.land.sample(latRad, lonRad) ?? 0
		if(land > 0) return land
		const sea = this.bathy.sample(latRad, lonRad)
		return sea === undefined ? 0 : Math.min(0, sea)
	}
}

function bathymetryTileUrl(tileId: TileId): string {
	const template = typeof localStorage !== "undefined" ? localStorage.getItem(cBathyUrlOverrideKey) : null
	if(template) {
		return template.replace("{z}", String(tileId.z)).replace("{y}", String(tileId.y)).replace("{x}", String(tileId.x))
	}
	return `${cTileServerHost}/bathymetry/${tileId.z}/${tileId.y}/${tileId.x}.webp`
}
